import { useEffect, useState, type RefObject } from "react";

/**
 * iOS-style collapsing title for the compact top bar.
 *
 * Pages render their own large title (the first `h1` in the page body). This
 * watches that heading and returns its text once it has scrolled up under the
 * top bar, so the bar can show it as a small centered title; it returns null
 * while the large title is still visible. The heading is re-found whenever
 * the page content changes (route changes, async page loads), so pages need
 * no wiring. Works with pages that scroll inside their own containers: the
 * intersection is measured against the viewport minus the top bar.
 */
export const useCollapsingPageTitle = (
  bodyRef: RefObject<HTMLElement>,
  topBarRef: RefObject<HTMLElement>,
  enabled: boolean
): string | null => {
  const [title, setTitle] = useState<string | null>(null);

  useEffect(() => {
    const body = bodyRef.current;
    if (!enabled || !body || typeof IntersectionObserver === "undefined") {
      setTitle(null);
      return;
    }

    let heading: HTMLElement | null = null;
    let observer: IntersectionObserver | null = null;
    let frame = 0;

    const observe = () => {
      const next = body.querySelector("h1");
      if (next === heading) return;
      observer?.disconnect();
      heading = next;
      setTitle(null);
      if (!heading) return;
      const barBottom = topBarRef.current?.getBoundingClientRect().bottom ?? 0;
      observer = new IntersectionObserver(
        ([entry]) => {
          const rootTop = entry.rootBounds?.top ?? barBottom;
          // Scrolled away upward (not merely below the fold).
          const scrolledPast =
            !entry.isIntersecting && entry.boundingClientRect.top < rootTop;
          setTitle(scrolledPast ? heading?.textContent?.trim() || null : null);
        },
        { rootMargin: `-${Math.round(barBottom)}px 0px 0px 0px` }
      );
      observer.observe(heading);
    };

    // Pages swap content on navigation and load async; re-find the heading
    // when the body changes (batched to one check per frame).
    const mutations = new MutationObserver(() => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(observe);
    });
    mutations.observe(body, { childList: true, subtree: true });
    observe();

    return () => {
      cancelAnimationFrame(frame);
      mutations.disconnect();
      observer?.disconnect();
    };
  }, [bodyRef, topBarRef, enabled]);

  return title;
};
